/** Terrain heightfield presets (editor Terrain panel): shared by panel UI and drag-to-scene placement. */

export const TERRAIN_PRESETS = [
  {
    key: "slope",
    label: "Slope",
    description:
      "Uniform inclined plane—consistent grade side to side. Use total height for a much steeper ramp.",
    nrow: 21,
    ncol: 22,
    elevation: 0.08,
    base: 0.1,
    defaultParams: { roughness: 0 },
  },
  {
    key: "rolling",
    label: "Rolling Hills",
    description: "Smooth low-frequency hills for gentle locomotion tests.",
    nrow: 33,
    ncol: 34,
    elevation: 0.18,
    base: 0.12,
    defaultParams: { frequency: 1.0, roughness: 0 },
  },
  {
    key: "rough",
    label: "Rough",
    description: "Mixed deterministic waves that create uneven ground.",
    nrow: 41,
    ncol: 42,
    elevation: 0.14,
    base: 0.12,
    defaultParams: { frequency: 1.0 },
  },
  {
    key: "hilly",
    label: "Hilly",
    description: "Several broad mounds with shallow valleys.",
    nrow: 49,
    ncol: 50,
    elevation: 0.28,
    base: 0.14,
    defaultParams: { frequency: 1.0, roughness: 0 },
  },
];

/**
 * @param {string} kind
 * @param {number} u  normalized [0,1] column coordinate
 * @param {number} v  normalized [0,1] row coordinate
 * @param {{ frequency?: number, roughness?: number }} [params]
 */
export function terrainSample(kind, u, v, params = {}) {
  const freq = Math.max(
    0.1,
    Number.isFinite(Number(params.frequency)) ? Number(params.frequency) : 1.0
  );
  const rough = Math.max(
    0,
    Math.min(1, Number.isFinite(Number(params.roughness)) ? Number(params.roughness) : 0.0)
  );

  if (kind === "slope") {
    const base = (u - 0.5) * 0.22 + (v - 0.5) * 0.14;
    const noise =
      rough * (Math.sin(u * Math.PI * 6.0 + 0.4) * 0.15 + Math.cos(v * Math.PI * 5.0 + 1.1) * 0.12);
    return base + noise;
  }

  const x = u * 2 - 1;
  const y = v * 2 - 1;

  if (kind === "rolling") {
    const base =
      Math.sin(u * Math.PI * 2.2 * freq) * 0.45 +
      Math.cos(v * Math.PI * 1.8 * freq) * 0.35 +
      Math.sin((u + v) * Math.PI * 1.2 * freq) * 0.2;
    const noise =
      rough *
      (Math.sin(u * Math.PI * 12.0 * freq + 0.7) * 0.3 +
        Math.cos(v * Math.PI * 11.0 * freq + 1.4) * 0.3);
    return base + noise;
  }

  if (kind === "rough") {
    return (
      Math.sin(u * Math.PI * 8.0 * freq + 0.4) * 0.24 +
      Math.cos(v * Math.PI * 7.0 * freq + 1.1) * 0.2 +
      Math.sin((u * 5.5 + v * 3.5) * Math.PI * freq) * 0.17 +
      Math.cos((x * x + y * y) * Math.PI * 5.0 * freq) * 0.1
    );
  }

  // hilly: gaussian mounds; higher frequency = narrower, sharper peaks
  const hills = [
    { cx: -0.46, cy: -0.18, r: 0.36 / freq, h: 0.9 },
    { cx: 0.22, cy: 0.36, r: 0.42 / freq, h: 0.75 },
    { cx: 0.58, cy: -0.44, r: 0.3 / freq, h: 0.55 },
    { cx: -0.08, cy: -0.58, r: 0.28 / freq, h: -0.35 },
  ];
  const base = hills.reduce((sum, hill) => {
    const dx = x - hill.cx;
    const dy = y - hill.cy;
    return sum + hill.h * Math.exp(-(dx * dx + dy * dy) / (2 * hill.r * hill.r));
  }, 0);
  const noise =
    rough * (Math.sin(u * Math.PI * 8.0 + 0.4) * 0.24 + Math.cos(v * Math.PI * 7.0 + 1.1) * 0.2);
  return base + noise;
}

function normalizeData(values) {
  let min = Infinity;
  let max = -Infinity;
  values.forEach((value) => {
    if (value < min) min = value;
    if (value > max) max = value;
  });
  const range = max - min;
  if (!Number.isFinite(range) || range <= 1e-9) return values.map(() => 0);
  return values.map((value) => (value - min) / range);
}

/** @param {{ frequency?: number, roughness?: number }} [params] */
export function buildTerrainData(kind, nrow, ncol, params = {}) {
  const values = [];
  for (let row = 0; row < nrow; row += 1) {
    const v = nrow <= 1 ? 0 : row / (nrow - 1);
    for (let col = 0; col < ncol; col += 1) {
      const u = ncol <= 1 ? 0 : col / (ncol - 1);
      values.push(terrainSample(kind, u, v, params));
    }
  }
  return normalizeData(values);
}
